import {
  ModelScoreFilterColumn,
  type ModelScoreFilterInput,
} from "@/generated/company-search";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import type { CompanyFilters } from "../types";

/**
 * The models a company-search query must carry in `model_scores`: the active
 * (displayed) models, plus any model a stored filter entry targets that isn't
 * displayed — deactivating a model's pill must not silently unfilter a saved
 * segment (RGI-2347), and hermes requires every filtered `modelId` to also be
 * in `modelScores`. Filter entries whose model is gone from the catalog can't
 * resolve and are left out (their chips gray instead).
 *
 * Feed the result to both `buildModelScores` and `buildModelScoreFilter` so
 * the two args stay consistent.
 */
export function withModelScoreFilterModels(
  scoringV2Enabled: boolean,
  activeModels: ScoringModel[],
  allModels: ScoringModel[],
  filters: CompanyFilters | undefined
): ScoringModel[] {
  if (!scoringV2Enabled) return activeModels;
  const entries = filters?.modelScoreFilterUi?.entries ?? [];
  if (entries.length === 0) return activeModels;

  const carried = new Set(activeModels.map((m) => m.family));
  const result = [...activeModels];
  for (const entry of entries) {
    if (carried.has(entry.modelId)) continue;
    const model = allModels.find((m) => m.family === entry.modelId);
    if (!model) continue;
    carried.add(model.family);
    result.push(model);
  }
  return result;
}

/**
 * Builds the top-level `modelScoreFilter` arg for `opportunityGeneratorSearch`
 * from the per-model UI slice (`filters.modelScoreFilterUi.entries`).
 *
 * Each entry targets one model; `models` is the set the query carries in
 * `modelScores` (see `withModelScoreFilterModels`) — entries outside it are
 * skipped, since hermes rejects a filter whose `modelId` isn't in
 * `modelScores`. Composite and each pillar (fit/need/intent) emit one entry
 * with inclusive int bounds, sent as-is; hermes ANDs every entry.
 *
 * Shared by CompaniesExploreList (list/matrix query), CompaniesExplorePage
 * (save-as-workbook cap gate) and CompanyExportModal so all count the same set.
 */
export function buildModelScoreFilter(
  scoringV2Enabled: boolean,
  models: ScoringModel[],
  filters: CompanyFilters | undefined
): ModelScoreFilterInput[] | undefined {
  if (!scoringV2Enabled || models.length === 0) return undefined;
  const entries = filters?.modelScoreFilterUi?.entries ?? [];
  if (entries.length === 0) return undefined;

  const carriedFamilies = new Set(models.map((m) => m.family));
  const items: ModelScoreFilterInput[] = [];

  for (const entry of entries) {
    if (!carriedFamilies.has(entry.modelId)) continue;

    const { modelId, tiers } = entry;
    for (const [column, range] of [
      [ModelScoreFilterColumn.COMPOSITE, entry.composite],
      [ModelScoreFilterColumn.FIT, entry.fit],
      [ModelScoreFilterColumn.NEED, entry.need],
      [ModelScoreFilterColumn.INTENT, entry.intent],
    ] as const) {
      if (range && (range.min !== undefined || range.max !== undefined)) {
        items.push({ modelId, column, min: range.min, max: range.max });
      }
    }

    if (tiers && tiers.length > 0) {
      const upper = tiers.map((t) => t.toUpperCase());
      items.push({
        modelId,
        column: ModelScoreFilterColumn.TIER,
        ...(upper.length === 1 ? { eq: upper[0] } : { in: upper }),
      });
    }
  }

  return items.length > 0 ? items : undefined;
}
